import {useState, useEffect, RefObject} from 'react';

export default function useLazyLoad<T extends HTMLElement>(
  isLazy: boolean,
  ref: RefObject<T>
) {
  const [isLoad, setIsLoad] = useState(!isLazy);
  const [isError, setIsError] = useState(false);
  useEffect(() => {
    const element = ref.current;
    function loadElement() {
      setIsLoad(true);
    }
    element && element.addEventListener(LOAD_ELEMENT_EVENT_TYPE, loadElement);
    return () => {
      element &&
        element.removeEventListener(LOAD_ELEMENT_EVENT_TYPE, loadElement);
    };
  }, [ref]);

  useEffect(() => {
    const element = ref.current;
    setIsLoad(!isLazy);
    setIsError(false);
    if (!isLazy || !element) {
      return;
    }

    if (!('IntersectionObserver' in window)) {
      setIsLoad(true);
      return;
    }
    const scrollParent = getScrollParent(element);
    let item = lazyObservers.find(item => item.scrollParent === scrollParent);
    if (!item) {
      const lazyObserver = new IntersectionObserver(onIntersection, {
        root: getScrollParent(element),
        rootMargin: '500px 100px',
      });
      item = {scrollParent, lazyObserver};
      lazyObservers.push(item);
      if (lazyObservers.length > 10) {
        lazyObservers.splice(0, 1);
      }
    }

    const lazyObserver = item.lazyObserver!;
    lazyObserver.observe(element);

    return () => {
      lazyObserver.unobserve(element);
    };
  }, [isLazy, ref]);

  return {canLoad: isLoad && !isError, setIsError};
}

// jquery scrollParent 코드 참고
// 링크: https://github.com/jquery/jquery-ui/blob/ed94edd98479516b42d689fade8569acaaaa1350/ui/scroll-parent.js
const REGEX_SCROLL = /(auto|scroll)/;
function getScrollParent(element: HTMLElement) {
  let style = getComputedStyle(element);
  if (style.position === 'fixed') return null;
  const isExcludeStaticParent = style.position === 'absolute';

  let parent: HTMLElement | null | undefined = element.parentElement;
  while (parent) {
    style = getComputedStyle(parent);
    if (isExcludeStaticParent && style.position === 'static') {
      parent = parent.parentElement;
      continue;
    }
    if (REGEX_SCROLL.test(style.overflow + style.overflowY + style.overflowX))
      return parent;
    parent = parent.parentElement;
  }
  return null;
}

const lazyObservers: Array<{
  scrollParent: HTMLElement | null;
  lazyObserver: IntersectionObserver | null;
}> = [];
const LOAD_ELEMENT_EVENT_TYPE = 'loadElement';

function onIntersection(
  entries: IntersectionObserverEntry[],
  io: IntersectionObserver
) {
  entries.forEach(entry => {
    if (entry.isIntersecting) {
      io.unobserve(entry.target);
      entry.target.dispatchEvent(new CustomEvent(LOAD_ELEMENT_EVENT_TYPE));
    }
  });
}
